import React, { FC, useEffect, useState } from 'react';

import { Button, Modal, Slider, ColorInput } from '../../../../shared/ui';
import {
  DPDG_SIZE_SLIDER_MAX,
  DPDG_SIZE_SLIDER_MIN,
  mapBallSizeToSliderStep,
  mapSliderStepToBallSize,
} from '../constants';

interface IBallSettingsModalProps {
  isShow: boolean;
  onClose: () => void;
  onApply: (settings: { color: string; size: number; speed: number }) => void;
  initialColor: string;
  initialSize: number;
  initialSpeed: number;
}

const tabs: Array<{ key: 'color' | 'size' | 'speed'; label: string }> = [
  { key: 'color', label: 'Цвет' },
  { key: 'size', label: 'Размер' },
  { key: 'speed', label: 'Скорость' },
];

const BallSettingsModal: FC<IBallSettingsModalProps> = ({
  isShow,
  onClose,
  onApply,
  initialColor,
  initialSize,
  initialSpeed,
}) => {
  const [activeTab, setActiveTab] = useState<'color' | 'size' | 'speed'>('color');
  const [color, setColor] = useState(initialColor);
  const [sizeStep, setSizeStep] = useState(() => mapBallSizeToSliderStep(initialSize));
  const [speed, setSpeed] = useState(initialSpeed);

  useEffect(() => {
    if (!isShow) {
      return;
    }

    setActiveTab('color');
    setColor(initialColor);
    setSizeStep(mapBallSizeToSliderStep(initialSize));
    setSpeed(initialSpeed);
  }, [isShow, initialColor, initialSize, initialSpeed]);

  const handleApply = () => {
    const nextSize = mapSliderStepToBallSize(sizeStep);
    onApply({ color, size: nextSize, speed });
  };

  const sizeInPixels = mapSliderStepToBallSize(sizeStep);

  return (
    <Modal
      isOpen={isShow}
      onClose={onClose}
      title="Настройка шара"
      description="Подберите цвет, размер и скорость движения для комфортной работы с инструментом."
      footer={
        <>
          <Button size="md" variant="neutral" onClick={onClose}>
            Отмена
          </Button>
          <Button size="md" variant="primary" onClick={handleApply}>
            ОК
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'dark' : 'neutral'}
              onClick={() => setActiveTab(tab.key)}
              size="md"
            >
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="min-h-[200px]">
          {activeTab === 'color' && (
            <div className="flex flex-col items-center gap-4 py-6">
              <ColorInput
                label="Выберите цвет шара"
                value={color}
                onChange={setColor}
                className="h-32 w-32 items-center justify-center rounded-full"
              >
                <span
                  className="block h-28 w-28 rounded-full border border-ink-subtle shadow-[inset_0_2px_8px_rgba(0,0,0,0.12)]"
                  style={{ backgroundColor: color }}
                />
              </ColorInput>
              <div className="text-ui-xs uppercase tracking-[0.08em] text-ink-muted">{color.toUpperCase()}</div>
            </div>
          )}

          {activeTab === 'size' && (
            <div className="flex h-full flex-col items-center gap-6 py-4">
              <div className="text-ui-strong text-ink">Размер: {sizeInPixels}</div>

              <Slider
                label="Размер шара"
                tone="neutral"
                min={DPDG_SIZE_SLIDER_MIN}
                max={DPDG_SIZE_SLIDER_MAX}
                value={sizeStep}
                onChange={setSizeStep}
              />

              <div className="relative flex items-center justify-center h-[140px] w-full">
                <div
                  className="rounded-full shadow-[0_4px_8px_rgba(0,0,0,0.25)] border border-ink-subtle transition-all duration-200"
                  style={{
                    backgroundColor: color,
                    width: `${sizeInPixels}px`,
                    height: `${sizeInPixels}px`,
                  }}
                />
              </div>
            </div>
          )}

          {activeTab === 'speed' && (
            <div className="flex h-full flex-col gap-6 py-4">
              <div className="text-ui-strong text-ink">Скорость: {speed}</div>

              <Slider label="Скорость шара" tone="neutral" min={1} max={40} value={speed} onChange={setSpeed} />

              <div className="flex justify-between text-ui-xs text-ink-muted">
                <span>Медленнее</span>
                <span>Быстрее</span>
              </div>

              <div className="relative w-full h-[100px] overflow-hidden mt-4 bg-ink-subtle/10 rounded-chip">
                <div
                  className="absolute top-1/2 -translate-y-1/2 rounded-full animate-move-ball"
                  style={{
                    backgroundColor: color,
                    width: '40px',
                    height: '40px',
                    animationDuration: `${Math.max(0.1, 5 / speed).toFixed(2)}s`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BallSettingsModal;
